import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logout } from '../store/slices/authSlice';
import { logoutUser, exportUserData } from '../services/api';
import AccountDeletion from './AccountDeletion';
import LanguageSwitcher from './LanguageSwitcher';
import { notify } from '../utils/notify';
import './Header.css';

function Header() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [showMenu, setShowMenu] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [dropdownAvatarError, setDropdownAvatarError] = useState(false);

  // Reset avatar error states when avatar URL changes
  useEffect(() => {
    setAvatarError(false);
    setDropdownAvatarError(false);
  }, [user?.avatar_url]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      // Call logout endpoint for server-side cleanup
      await logoutUser();
    } catch (err) {
      // Even if API call fails, proceed with client-side logout
      console.error('Logout API error:', err);
    } finally {
      // Client-side cleanup
      dispatch(logout());
      setLoggingOut(false);
      setShowMenu(false);
      navigate('/login');
    }
  };

  const handleExportData = async () => {
    setExportingData(true);
    setShowMenu(false);
    try {
      await exportUserData();
      // Success is handled in the API function (download triggered)
    } catch (err) {
      // ``exportUserData`` decodes blob error bodies, so ``err.message``
      // is the real backend detail. Show it after the generic label so
      // a 401/500 is actionable instead of opaque.
      console.error('Failed to export data:', err);
      const detail = err?.message;
      notify.error(detail ? `${t('header.export_failed')}\n\n${detail}` : t('header.export_failed'));
    } finally {
      setExportingData(false);
    }
  };

  // Show login button if not authenticated or no user data
  if (!isAuthenticated || !user) {
    return (
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-logo" onClick={() => navigate('/')}>
              <img 
                src="/images/ai-logo.png" 
                alt="Smart Gain Community" 
                className="header-logo-img"
              />
              <span className="header-logo-text">Smart Gain Community</span>
            </div>
          </div>
          <div className="header-right">
            <LanguageSwitcher />
            <button
              className="admin-link-button"
              onClick={() => navigate('/admin/login')}
            >
              {t('header.admin')}
            </button>
            <button
              className="login-button"
              onClick={() => navigate('/login')}
            >
              {t('header.login')}
            </button>
          </div>
        </div>
      </header>
    );
  }

  const userDisplayName = user?.full_name || user?.email || 'User';

  return (
    <>
    <header className="header">
      <div className="header-content">
        <div className="header-left">
          <div className="header-logo" onClick={() => navigate('/')}>
            <img 
              src="/images/ai-logo.png" 
              alt="Smart Gain Community" 
              className="header-logo-img"
            />
            <span className="header-logo-text">Smart Gain Community</span>
          </div>
        </div>
        
        <div className="header-right">
          <LanguageSwitcher />
          <div className="user-menu-container">
            <button
              className="user-menu-button"
              onClick={() => setShowMenu(!showMenu)}
              aria-label="User menu"
            >
              <div className="user-avatar">
                {user?.avatar_url && !avatarError ? (
                  <img 
                    src={user.avatar_url} 
                    alt={userDisplayName}
                    onError={() => setAvatarError(true)}
                  />
                ) : null}
                <span style={{ display: (user?.avatar_url && !avatarError) ? 'none' : 'flex' }}>
                  {userDisplayName.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="user-name">{userDisplayName}</span>
              <svg 
                className={`menu-arrow ${showMenu ? 'open' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showMenu && (
              <>
                <div 
                  className="menu-overlay"
                  onClick={() => setShowMenu(false)}
                />
                <div className="user-dropdown">
                  <div className="dropdown-header">
                    <div className="dropdown-avatar">
                      {user?.avatar_url && !dropdownAvatarError ? (
                        <img 
                          src={user.avatar_url} 
                          alt={userDisplayName}
                          onError={() => setDropdownAvatarError(true)}
                        />
                      ) : null}
                      <span style={{ display: (user?.avatar_url && !dropdownAvatarError) ? 'none' : 'flex' }}>
                        {userDisplayName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="dropdown-user-info">
                      <div className="dropdown-name">{userDisplayName}</div>
                      {user?.email && (
                        <div className="dropdown-email">{user.email}</div>
                      )}
                    </div>
                  </div>
                  <div className="dropdown-divider" />
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      navigate('/profile');
                      setShowMenu(false);
                    }}
                  >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {t('header.profile_settings')}
                  </button>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      navigate('/account/security');
                      setShowMenu(false);
                    }}
                  >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    {t('header.security_devices')}
                  </button>
                  {(user?.role === 'admin' || user?.role === 'superadmin') && (
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        navigate('/admin/dashboard');
                        setShowMenu(false);
                      }}
                    >
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      {t('header.admin_dashboard')}
                    </button>
                  )}
                  <button
                    className="dropdown-item"
                    onClick={handleExportData}
                    disabled={exportingData}
                  >
                    {exportingData ? (
                      <>
                        <svg className="spinner" fill="none" viewBox="0 0 24 24">
                          <circle className="spinner-circle" cx="12" cy="12" r="10" />
                        </svg>
                        {t('header.exporting')}
                      </>
                    ) : (
                      <>
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {t('header.export_data')}
                      </>
                    )}
                  </button>
                  <button
                    className="dropdown-item delete-account-button"
                    onClick={() => {
                      setShowDeletionModal(true);
                      setShowMenu(false);
                    }}
                  >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {t('header.delete_account')}
                  </button>
                  <div className="dropdown-divider" />
                  <button
                    className="dropdown-item logout-button"
                    onClick={handleLogout}
                    disabled={loggingOut}
                  >
                    {loggingOut ? (
                      <>
                        <svg className="spinner" fill="none" viewBox="0 0 24 24">
                          <circle className="spinner-circle" cx="12" cy="12" r="10" />
                        </svg>
                        {t('header.logging_out')}
                      </>
                    ) : (
                      <>
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        {t('header.logout')}
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
          </div>
        </div>
      </header>
      <AccountDeletion
        isOpen={showDeletionModal}
        onClose={() => setShowDeletionModal(false)}
      />
    </>
  );
}

export default Header;

